/**
 * 押したあとに開いた節まで、画面を動かす。
 *
 * ## なぜ要るか
 *
 * 一覧の行で［管理］を押すと、選んだ会社の節は**画面のずっと下に生えて**いた。
 * 画面は動かないので、パソコンでも「押したのに何も起きない」に見え、
 * スマホでは一覧しか見えないため<b>まったく変化が無い</b>ように感じる
 * （2026-09-18、利用者の指摘）。開くだけでなく、そこへ連れて行く。
 *
 * ## 使い方
 *
 *   import { revealSection } from '@/utils/revealSection'
 *   ...
 *   this.selectedAccount = item
 *   revealSection(this, 'selectedAccountCard')   // ref の名前
 *
 * 描き終わってから動かすので、呼ぶのは値を入れた直後でよい。
 * 通信の結果を待つ必要はない（待つと、押してから動くまでが遠くなる）。
 */

/** 画面を動かすときの間合い。上に少し余白を残すと、見出しが帯の下に隠れない。 */
const MARGIN_PX = 12

function scrollToElement (element) {
  if (!element || typeof element.getBoundingClientRect !== 'function') return false
  /*
   * **スクロールは、できる方法で行う。**jsdom（試験）や古い端末には
   * scrollIntoView が無い。無いことでエラーにすると、開く動作まで巻き添えになる。
   */
  if (typeof element.scrollIntoView === 'function') {
    try {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      return true
    } catch (error) {
      element.scrollIntoView(true)
      return true
    }
  }
  if (typeof window !== 'undefined' && typeof window.scrollTo === 'function') {
    const top = element.getBoundingClientRect().top + (window.pageYOffset || 0) - MARGIN_PX
    window.scrollTo(0, Math.max(0, top))
    return true
  }
  return false
}

/**
 * 開いた節へ画面を動かす。
 *
 * @param {object} vm   呼び出し元のコンポーネント（this）
 * @param {string} name $refs の名前
 * @returns {Promise<boolean>} 動かせたか（試験で見る）
 */
export function revealSection (vm, name) {
  if (!vm || typeof vm.$nextTick !== 'function') return Promise.resolve(false)
  return vm.$nextTick().then(() => {
    const target = vm.$refs ? vm.$refs[name] : null
    /* CoreUI の部品は ref がコンポーネントで返るので、実体の要素まで降りる。 */
    const element = Array.isArray(target)
      ? (target[0] && target[0].$el ? target[0].$el : target[0])
      : (target && target.$el ? target.$el : target)
    return scrollToElement(element)
  })
}

export default revealSection
